import React from "react";

import DatePicker, { registerLocale } from "react-datepicker";
import DatePickerCustomHeader from "./components.v2/custom.header";
import DatePickerCustomInput from "./components.v2/custom.input.single";
import DateRangePickerCustomInput from "./components.v2/custom.input.double";

import { filterDate, getDayClassnameForDateRangePicker, getDayClassnameForDatepicker, getDayNameLocalized } from "./date.utils";
import { ApplicationContext } from "src/context";

import { isRunTime } from "src/utils/isRunTime";
import { addListener, removeListener } from "src/utils/events";
import { noUndefinedsOrEmptyStrings } from "src/utils/objects";
import { scrollTo } from "src/utils/scroll";
import CTAButton from "src/components/ctabutton/ctabutton";

import "./DatePicker.v2.scss";

type DatePickerV2Props = {
    /** is range picker */
    isRange: boolean;

    /** auto configured for isrange (2) and single (1) */
    monthsShown?: number;

    /** availability array */
    availability?: any[] | undefined;

    /** weeksday blocked */
    weekdaysBlocked?: any[] | undefined;

    /** input label for single date mode */
    label: string;

    /** input value for single date mode */
    value?: Date | undefined;

    /** start date value for range date mode */
    startDate: Date | undefined;

    /** start date label for range date mode */
    startLabel?: string;

    /** end date value for range date mode */
    endDate: Date | undefined;

    /** end date label for range date mode */
    endLabel?: string;

    /** locale to use */
    locale?: string;

    /** on change */
    onChange: ((date: Date, event: React.SyntheticEvent<any> | undefined) => void) | ((date: Date[], event: React.SyntheticEvent<any> | undefined) => void),

    /** include date intervals */
    includeDateIntervals: Array<{start: Date, end: Date}> | boolean,

    /** exclude date intervals */
    excludeDateIntervals: Array<{start: Date, end: Date}>,

    /** open to date */
    openToDate: Date | undefined,

    /** min number of days, which will only affect the departure day to prevent blocking re-selections */
    minDays?: number | undefined,

    /** max number of days, which will only affect the departure day to prevent blocking re-selections  */
    maxDays?: number | undefined,

    /** disable keyboard? */
    keyboardDisabled?: boolean,

    /** input background color */
    inputBackgroundColor?: string,

    /** input text color */
    inputTextColor?: string,

    /** input placeholder color */
    inputPlaceholderColor?: string,

    /** input chevron color */
    inputChevronColor?: string,

    /* calendar chevron colors */
    calendarChevronColor?: string;

    /** input border color */
    inputBorderColor?: string,

    /** selected day color */
    selectedDayColor?: string,

    /** selected day bg color */
    selectedDayBgColor?: string,

    /** day hover bg color  */
    hoverDayBgColor?: string,

    /** day hover color */
    hoverDayColor?: string,

    /** border radius px of days */
    markerStyle?: string,

    /** "border bottom style" for the line between days (except the last week) */
    lineBetweenDays?: string;

    /** displays chevrons in the right or one left and one right */
    cursorPosition?: "side_to_side" | "right";

    /** size of the days in px (default 46px)  */
    daySize?: string;

    /** size of the margin in days in px (default 0.166rem)  */
    dayMargin?: string;

    /** size of the margin in day of the week boxes (default 0.166rem) */
    dayOfWeekMargin?: string;

    /** size of the margin of the box that wraps all the boxes of the days (1 to 31...) (default 0.4rem) */
    monthMargin?: string;

    /** removes picker border  */
    removeBorder?: boolean;

    /** is calendar inline */
    inline?: boolean,

    /** removes scroll behaviour when opening/closing the calendar */
    disableScroll?: boolean

    /** check if day is avaible (fn hook) */
    isDayBlocked?: (date: any) => boolean;

    inputClassname?: string;
    inputFontClassname?: string;

    removeClearSelection?: boolean;
    removeMinDate?: boolean
}


type DatePickerState = {
    /** is datepicker open */
    open: boolean,
    /** what date is hovering */
    hoverDate: Date | undefined,
    /** which device is detected */
    device: "desktop" | "mobile",
    /** last scroll position */
    scroll: number | undefined,
}

/**
 * Custom Datepicker v2
 */
export default class DatePickerV2 extends React.Component<DatePickerV2Props, DatePickerState> {

    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;

    static MOBILE_WIDTH = 1024;

    public state: DatePickerState = {
        open: false,
        hoverDate: undefined,
        device: isRunTime() ? (window.innerWidth >= DatePickerV2.MOBILE_WIDTH ? "desktop" : "mobile") : "mobile",
        scroll: undefined,
    }

    static defaultProps: Partial<DatePickerV2Props> = {
        includeDateIntervals: [],
        excludeDateIntervals: [],
        availability: [],
        weekdaysBlocked: [],
        value: undefined,
        locale: "en-US",
        cursorPosition: "side_to_side"
    }

    private headerRef = React.createRef<DatePickerCustomHeader>();

    clearDates = () => {
        if (this.props.onChange) {
            this.props.onChange([null, null], undefined); // Clears both startDate and endDate
        }
    }

    calendarContainer = ({ children, className }: { children: React.ReactNode, className: string }) => {
        return (
            <div className={className}>
                {children} {/* Render the calendar */}
                {!this.props.removeClearSelection &&
                    <div style={{ padding: "10px", textAlign: "center" }}>
                        <CTAButton icon={<React.Fragment></React.Fragment>} className="mx-auto !text-sm" useButton onClick={() => this.clearDates()} type="soft" text={"Clear selection"} />
                    </div>
                }
            </div>
        )

    };

    scrollToCalendar() {
        if (!this.context.isMobile) return;
        setTimeout(() => this.setState({ scroll: this.headerRef.current?.scrollIntoView() }), 100)
    }

    restoreScroll() {
        if (!this.context.isMobile) return;
        scrollTo({top: this.state.scroll, behavior: 'auto'});
        this.setState({scroll: undefined});
    }

    private onResize = () => {
        if (this.state.device === 'desktop' && window.innerWidth < DatePickerV2.MOBILE_WIDTH) return this.setState({device: 'mobile'});
        if (this.state.device === 'mobile' && window.innerWidth >= DatePickerV2.MOBILE_WIDTH) return this.setState({device: 'desktop'});
    }

    private onDateChanged(date: any, event: React.SyntheticEvent<any> | undefined) {
        if (this.props.onChange) this.props.onChange(date, event);
    }

    componentDidMount() {
        addListener('resize', this.onResize);
        this.loadLocale();
    }

    componentWillUnmount(): void {
        removeListener('resize', this.onResize);
    }

    private get locale() {
        return this.props.locale || "en-US";
    }

    private async loadLocale() {
        const locale = this.locale;
        try {
            const localeToSet: any = await import(`date-fns/locale/${locale}/index.js`);
            registerLocale(locale as string, localeToSet);
            this.forceUpdate();
        } catch (err) {
            const localeToSet: any = await import(`date-fns/locale/en-US/index.js`);
            registerLocale(locale, localeToSet);
            this.forceUpdate();
        }
    }

    private onDayMouseEnter(date: any) {
        if (!this.props.startDate || this.props.endDate) return;
        if (this.props.startDate < date) {
            this.setState({hoverDate: date});
        } else {
            this.setState({hoverDate: this.props.startDate});
        }
    }

    private onDayMouseLeave() {
        this.setState({hoverDate: undefined})
    }

    private getMinDate = (): Date | null => {
        if (!this.props.minDays || (this.props.startDate && this.props.endDate) || (!this.props.startDate && !this.props.endDate)) {
          return new Date();
        }
        if (this.props.startDate) {
          const minDate = new Date(this.props.startDate);
          minDate.setDate(minDate.getDate() + this.props.minDays);
          return minDate;
        }
        return null;
    }

    private getMaxDate = (): Date | null => {
        if (!this.props.maxDays || (this.props.startDate && this.props.endDate) || (!this.props.startDate && !this.props.endDate)) {
          return null;
        }
        if (this.props.startDate) {
          const maxDate = new Date(this.props.startDate);
          maxDate.setDate(maxDate.getDate() + this.props.maxDays);
          return maxDate;
        }
        return null;
      }

    /** Configure calendar colors here: */
    private get style(): React.CSSProperties {
        return noUndefinedsOrEmptyStrings({
            /** input */
            '--input-background-color': this.props.inputBackgroundColor || '#fff',
            '--input-border-color': this.props.inputBorderColor || '#EAEAF2',
            '--input-text-color': this.props.inputTextColor || '#525258',
            '--input-placeholder-color': this.props.inputPlaceholderColor || '#A6A6AE',
            /** header: */
            '--cal-header-bg-color': undefined,
            '--cal-header-color': undefined,
            /** chevron color */
            '--input-chevron-color': this.props.inputChevronColor || 'var(--primaryColor)',
            '--cal-chevron-color': this.props.calendarChevronColor || 'var(--primaryColor)',
            /** day names */
            '--cal-daynames-color': undefined,
            /** selected day: */
            '--cal-selected-day-bgcolor': this.props.selectedDayBgColor || 'var(--primaryColor)',
            '--cal-selected-day-color': this.props.selectedDayColor || '#FFFFFF',
            /** day */
            '--hover-background-color': this.props.hoverDayBgColor,
            '--hover-color': this.props.hoverDayColor,
            '--day-border-radius': this.props.markerStyle,
            '--day-size': this.props.daySize,
            '--day-margin': this.props.dayMargin,
            /** day of the week */
            '--day-week-margin': this.props.dayOfWeekMargin,
            /** month */
            '--month-wrapper-margin': this.props.monthMargin,
            /** week */
            '--line-between-days': this.props.lineBetweenDays,
            /** keyboard helper */
            '--cal-kbselected-day-bgcolor': /*this.props.selectedDayBgColor || 'var(--primaryColor)'*/ "transparent",
            '--cal-kbselected-day-color': "#000",//this.props.selectedDayColor,
            /** ranges: */
            '--cal-range-day-bgcolor': undefined,
            '--cal-range-day-color': undefined,
            '--cal-range-start-bgcolor': this.props.selectedDayBgColor || 'var(--primaryColor)',
            '--cal-range-start-color': this.props.selectedDayColor,
            '--cal-range-end-bgcolor': this.props.selectedDayBgColor || 'var(--primaryColor)',
            '--cal-range-end-color': this.props.selectedDayColor,
            /** container */
            '--border': this.props.removeBorder ? "none" : undefined
        });
    }

    render(): React.ReactNode {
        return <>
        <div className="datepickerv2-calendar" style={this.style}>
            <DatePicker
                id={`datepickerv2-calendar-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`}
                locale={this.locale}
                openToDate={this.props.openToDate}
                selectsRange={this.props.isRange}
                monthsShown={this.state.device === "mobile" ? 1 : this.props.monthsShown ? this.props.monthsShown : this.props.isRange ? 2 : 1}
                startDate={this.props.startDate}
                endDate={this.props.endDate}
                selected={this.props.isRange ? this.props.startDate : this.props.value}
                onChange={(date, event) => this.onDateChanged(date, event)}
                formatWeekDay={(day) => getDayNameLocalized(day as any, this.locale)}
                calendarStartDay={1}
                popperPlacement="bottom"
                popperModifiers={[ { name: "flip", options: { allowedAutoPlacements: ["bottom"], fallbackPlacements: ["bottom"] } }, ]}
                showPopperArrow={false}
                calendarClassName={`bg-white rounded-lg overflow-hidden ${this.props.isRange ? 'moderange' : ''}`}
                customInput={
                    this.props.isRange
                    ? <DateRangePickerCustomInput
                        startPlaceholder={this.props.startLabel as string}
                        endPlaceholder={this.props.endLabel as string}
                        locale={this.locale}
                        startDate={this.props.startDate}
                        endDate={this.props.endDate}
                        keyboardDisabled={this.props.keyboardDisabled}
                        className={this.props.inputClassname}
                        fontClassname={this.props.inputFontClassname || ""}
                        />
                    : <DatePickerCustomInput label={this.props.label} date={this.props.value} value={this.props.value} keyboardDisabled={this.props.keyboardDisabled} className={this.props.inputClassname} fontClassname={this.props.inputFontClassname || ""}/>}
                disabledKeyboardNavigation={this.props.keyboardDisabled}
                includeDateIntervals={this.props.includeDateIntervals}
                excludeDateIntervals={this.props.excludeDateIntervals}
                dayClassName={(date) =>
                    this.props.isRange
                        ? getDayClassnameForDateRangePicker(date, this.state.hoverDate, this.props.startDate, this.props.endDate, this.props.availability, this.props.weekdaysBlocked)
                        : getDayClassnameForDatepicker(date, this.props.value, this.props.availability, this.props.weekdaysBlocked)
                }
                minDate={this.props.removeMinDate ? null : this.getMinDate()}
                maxDate={this.getMaxDate()}
                filterDate={(date) => filterDate(date, this.props.weekdaysBlocked, this.props.availability, this.props.isDayBlocked)}
                onDayMouseEnter={(date) => this.onDayMouseEnter(date)}
                onMonthMouseLeave={() => this.onDayMouseLeave()}
                inline={this.props.inline}
                calendarContainer={this.calendarContainer} // Use custom calendar container here
                renderCustomHeader={({ ...props }) => (
                    <DatePickerCustomHeader
                        ref={this.headerRef}
                        monthsShown={this.props.monthsShown ? this.props.monthsShown : this.props.isRange ? 2 : 1}
                        cursorPosition={this.props.cursorPosition}
                        device={this.state.device}
                        locale={this.locale as string}
                        style={this.style}
                        {...props}
                    />
                )}
                onCalendarOpen={() => !this.props.disableScroll ? this.scrollToCalendar() : null}
                onCalendarClose={() => !this.props.disableScroll ? this.restoreScroll() : null}
            />
        </div>
        </>
    }
}